import React, { useContext } from "react";
import { Button, ButtonProps } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { TryBuyContext } from "./TryBuyContainer";
// import useStyles from "../styles";

/**
 * PM5 T2 (money path, batch "onboarding") — converted from MUI v4 to MUI v5+
 * (`@mui/material`). See docs/mui-conversion-guide.md.
 *
 * **SUBSCRIPTIONS ESCALATION CLASS.** The CTA that opens the trial / notify-me
 * dialog. Conversion is styling-only: the context call, the `modalType`
 * default and the prop spread are unchanged.
 */

/**
 * PARITY RESTORE (guide §2, "Button default color"). `TryButton` renders a
 * `<Button>` with no `color` prop, so in v4 it was `color="default"` →
 * `color: theme.palette.text.primary`. v5+ deleted `default` and made
 * `primary` the default, which on this theme is #99ca3c — the same lime that
 * `themeV5`'s `MuiButton.root` paints as the background. Without this pin
 * every "Try FarmFLiX" / "72H Free Trial" CTA on the site renders lime on
 * lime, i.e. invisible.
 *
 * It is declared BEFORE the prop spread so a caller-supplied `sx` still wins.
 *
 * ⚠ REVIEW NOTE — one known caller conflict, deliberately not "fixed" here.
 * `Home/Marquee.js` (still v4, converts in T4) passes
 * `className={classes.buyButton}`, whose JSS rule sets `color: white` for the
 * mobile "Buy Now" text link. In v4 that class always beat MUI's own rule; now
 * it is a JSS class competing with an emotion class at the same specificity,
 * so the winner is stylesheet insertion order rather than a guarantee. Every
 * OTHER caller class (`classes.button`, `classes.ctaButton`, …) sets no
 * `color` at all and needs this restore, so the pin stays — Marquee's one
 * button is called out for the T4 conversion, where the fix is to move that
 * class's `color` onto the element's own `sx`.
 */
const tryButtonDefaultColorSx: SxProps<Theme> = { color: "text.primary" };

type TryButtonProps = Omit<ButtonProps, "onClick" | "children"> & {
  label: string;
  modalType?: "trial" | "notify";
};

function TryButton({ label, modalType = "trial", ...props }: TryButtonProps) {
  const { setDialogOpen } = useContext(TryBuyContext);

  return (
    <Button
      onClick={() => {
        setDialogOpen(modalType);
      }}
      sx={tryButtonDefaultColorSx}
      {...props}
      size={props.size}
    >
      {label}
    </Button>
  );
}

export default TryButton;
